/**
 * Per-selection pet/vehicle type icons for the household surfaces (Household
 * card + header badge popovers): the glyph mirrors the PropertyStep type cards
 * so a fish shows a fish, not a generic paw. Unknown/absent types fall back to
 * the generic PawPrint / Car. Icons inherit the parent's text color; sizing
 * comes from the consumer's `[&>svg]:size-4` wrapper (MaskIcon spans carry
 * their own size-4 since that selector only reaches svg children).
 */
import { Bird, Car, Cat, Dog, PawPrint, Truck } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '#app/utils/misc'

/** SVG file as a CSS mask so it inherits the parent's text color — same
 * helper as PropertyStep / PropertyReviewCard, for the type icons that
 * aren't lucide components (Fish / Reptile / Motorbike). */
function MaskIcon({ src, className }: { src: string; className?: string }) {
	return (
		<span
			aria-hidden
			className={cn('inline-block bg-current', className)}
			style={{
				maskImage: `url(${src})`,
				WebkitMaskImage: `url(${src})`,
				maskSize: 'contain',
				WebkitMaskSize: 'contain',
				maskRepeat: 'no-repeat',
				WebkitMaskRepeat: 'no-repeat',
				maskPosition: 'center',
				WebkitMaskPosition: 'center',
			}}
		/>
	)
}

export function petTypeIcon(petType: string | undefined): ReactNode {
	switch (petType?.trim().toLowerCase()) {
		case 'dog':
			return <Dog />
		case 'cat':
			return <Cat />
		case 'bird':
			return <Bird />
		case 'fish':
			return <MaskIcon src="/img/Fish.svg" className="size-4" />
		case 'reptile':
			return <MaskIcon src="/img/Reptile.svg" className="size-4" />
		default:
			return <PawPrint />
	}
}

export function vehicleTypeIcon(vehicleType: string | undefined): ReactNode {
	switch (vehicleType?.trim().toLowerCase()) {
		case 'motorbike':
			return <MaskIcon src="/img/Motorbike.svg" className="size-4" />
		case 'truck':
			return <Truck />
		default:
			return <Car />
	}
}
